import type { DayClassification, DayStatus } from '@/types/monthly'
import { isWeekendYMD } from '@/lib/date-utils'

export interface Break {
  start: string | null
  end: string | null
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

export function totalBreakMinutes(breaks: Break[] | undefined | null): number {
  if (!Array.isArray(breaks)) return 0
  let total = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      const diff = timeToMinutes(b.end) - timeToMinutes(b.start)
      if (diff > 0) total += diff
    }
  }
  return total
}

export const MANDATORY_BREAK_MINUTES = 60
// Shifts shorter than (or equal to) this gross duration only deduct *logged* breaks.
// Longer shifts get the mandatory 1hr lunch auto-applied (max of logged vs mandatory).
export const SHORT_SHIFT_THRESHOLD_MINUTES = 5 * 60

export function computeHours(checkIn: string | null, checkOut: string | null, breaks?: Break[]): number {
  if (!checkIn || !checkOut) return 0
  const grossMin = timeToMinutes(checkOut) - timeToMinutes(checkIn)
  if (grossMin <= 0) return 0
  const logged = Math.max(0, totalBreakMinutes(breaks))
  // Short shifts (e.g. half-days, 4-hour interns): only deduct logged breaks.
  // Long shifts: assume an unlogged 1hr lunch unless the user logged more.
  const breakDeduct = grossMin > SHORT_SHIFT_THRESHOLD_MINUTES
    ? Math.max(MANDATORY_BREAK_MINUTES, logged)
    : logged
  const diffMin = grossMin - breakDeduct
  if (diffMin <= 0) return 0
  return Math.round((diffMin / 60) * 10) / 10
}

export interface AttendanceRecord {
  checkIn: string | null
  checkOut: string | null
  workMode: string | null
  breaks?: Break[]
}

export interface ClassifyDayInput {
  date: string
  record: AttendanceRecord | null
  leaveDates: Set<string>
  wfhDates: Set<string>
  holidayDates: Set<string>
  override?: { checkOut: string }
  /** Explicit per-day status an admin/HR wrote via the attendance override
   *  (PRESENT / LEAVE / HOLIDAY / WEEKEND). Only honored on days without a
   *  check-in — a check-in always means PRESENT. ABSENT is excluded because the
   *  store defaults missing statuses to ABSENT and that default must not mask
   *  WFH_NO_LOG on approved-WFH days. */
  statusOverride?: string | null
}

function isWeekend(date: string): boolean {
  // Sunday is the only weekly off; Saturday is a working day. Centralized in
  // date-utils so the scheduler and working-day math agree with the calendar.
  return isWeekendYMD(date)
}

export function classifyDay(input: ClassifyDayInput): DayClassification {
  const base = {
    date: input.date,
    workMode: null as string | null,
    hours: 0,
    incomplete: false,
    checkIn: null as string | null,
    checkOut: null as string | null,
    overrideApplied: false,
  }

  if (isWeekend(input.date)) {
    return { ...base, status: 'WEEKEND' as DayStatus }
  }
  if (input.holidayDates.has(input.date)) {
    return { ...base, status: 'HOLIDAY' as DayStatus }
  }

  // An explicit HR-set status beats the inferred leave/WFH/absence logic below.
  const explicit = input.statusOverride ? String(input.statusOverride).toUpperCase() : null
  if (!input.record?.checkIn && (explicit === 'PRESENT' || explicit === 'LEAVE' || explicit === 'HOLIDAY' || explicit === 'WEEKEND')) {
    return { ...base, status: explicit as DayStatus, workMode: explicit === 'PRESENT' ? 'OFFICE' : null }
  }

  if (input.leaveDates.has(input.date)) {
    return { ...base, status: 'LEAVE' as DayStatus }
  }

  const wfhApproved = input.wfhDates.has(input.date)
  const hasCheckIn = !!input.record?.checkIn

  if (wfhApproved && !hasCheckIn) {
    return {
      ...base,
      status: 'WFH_NO_LOG' as DayStatus,
      incomplete: true,
      workMode: 'WFH',
    }
  }

  if (hasCheckIn) {
    const checkIn = input.record!.checkIn
    let checkOut = input.record!.checkOut
    let overrideApplied = false
    if (!checkOut && input.override?.checkOut) {
      checkOut = input.override.checkOut
      overrideApplied = true
    }
    const hours = computeHours(checkIn, checkOut, input.record!.breaks)
    const incomplete = !checkOut || hours === 0
    return {
      ...base,
      status: 'PRESENT' as DayStatus,
      workMode: wfhApproved ? 'WFH' : (input.record!.workMode || 'OFFICE'),
      hours,
      incomplete,
      checkIn,
      checkOut,
      overrideApplied,
    }
  }

  return { ...base, status: 'ABSENT' as DayStatus }
}
